import React from "react";
import { motion } from "framer-motion";
import { Film, Clapperboard, GraduationCap } from "lucide-react";

const experiences = [
  {
    icon: Film,
    period: "Current",
    role: "AI Artist / AI Lead",
    company: "RAAKA Movie",
    title: "Leading AI workflows for RAAKA.",
    desc: "Leading AI ideation and visual development workflows including AI-VFX shots, director requirement explorations, in-house AI previz, and edit placeholder creation. Designing photoreal cinematic prompts and shot concepts with attention to camera language, lighting continuity, production feasibility, and VFX integration.",
  },
  {
    icon: Clapperboard,
    period: "Current / Recent",
    role: "Assistant Director (VFX)",
    company: "Atlee Direction Team",
    title: "VFX-focused direction support.",
    desc: "Handling shot ideation, workflow management, and visual communication between creative intent and technical execution. Preparing cinematic frames, reference-based look development, action shot plans, creature/fantasy explorations, and production-friendly VFX references.",
  },
  {
    icon: GraduationCap,
    period: "Education",
    role: "Computer Science Engineering",
    company: "Technical Foundation",
    title: "Creative work backed by systems thinking.",
    desc: "Computer science foundation supports AI pipeline planning, prompt engineering, custom ComfyUI/WebUI workflows, tool troubleshooting, and scalable AI output across demanding film schedules.",
  },
];

export default function ExperienceSection() {
  return (
    <section
      id="experience"
      data-testid="experience-section"
      className="py-24 sm:py-32 px-6 sm:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="section-number mb-4">02 &middot; Experience</p>
          <h2
            className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none text-white max-w-3xl"
            data-testid="experience-heading"
          >
            Current film production{" "}
            <span className="font-serif-accent text-amber-400">
              experience.
            </span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-neutral-400 leading-relaxed max-w-2xl">
            A practical mix of creative ideation, VFX-focused direction support,
            AI pipeline handling, art department coordination, and editing report
            preparation.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="mt-16 relative">
          {/* Vertical line */}
          <div className="absolute left-[5px] top-0 bottom-0 w-px bg-white/10 hidden sm:block" />

          <div className="space-y-12">
            {experiences.map((exp, i) => (
              <motion.div
                key={exp.role}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`experience-item-${i}`}
                className="relative sm:pl-12"
              >
                {/* Dot */}
                <div className="absolute left-0 top-2 amber-dot hidden sm:block" />

                <div className="glass-surface glass-surface-hover rounded-2xl p-8 transition-all duration-500">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="text-xs font-mono tracking-[0.15em] uppercase text-amber-500/80 px-3 py-1 bg-amber-500/10 rounded-full">
                      {exp.period}
                    </span>
                    <span className="text-sm text-white font-medium">
                      {exp.role}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <exp.icon size={18} className="text-neutral-500" />
                    <span className="text-sm text-neutral-400">
                      {exp.company}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl tracking-tight text-white mb-3">
                    {exp.title}
                  </h3>
                  <p className="text-sm text-neutral-400 leading-relaxed">
                    {exp.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
